import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { getProfileRoutines } from '../features/profiles/routineMutations';
import { useActiveProfile } from '../features/profiles/useActiveProfile';
import { padCompleted } from '../features/progress/padCompleted';
import { dateKey } from '../lib/dateKey';
import { loadDailyProgress } from '../lib/storage/indexedDb';
import type { Id, Routine } from '../lib/types';
import { useSyncedStore } from '../store/synced';
import { resolveTheme } from '../themes/resolveTheme';

// Per-profile routine list. Per plan/03 §Screen inventory: kid picks Aamu /
// Ilta / … here. Routines that are *ongoing today* (started but not yet
// finished) get a pulsing highlight so the kid spots where they left off.
// Stale URL (deleted profile, wrong id) routes home.
export function RoutineSelectorPage() {
  const { profileId } = useParams();
  useActiveProfile(profileId);
  const tree = useSyncedStore((s) => s.tree);
  const profile = tree?.profiles.find((p) => p.id === profileId);
  const routines = tree && profile ? getProfileRoutines(tree, profile.id) : [];
  const ongoing = useOngoingRoutineIds(profile?.id, routines);

  if (!profile) return <Navigate to="/" replace />;

  const columns = routines.length <= 3 ? 1 : 2;
  // Selector lists every routine, so no per-routine override applies here.
  const palette = resolveTheme(profile).routineCardPalette;

  return (
    <main className="routines" style={{ ['--profile-color' as string]: profile.color }}>
      <Link to="/" className="back-button" aria-label="Takaisin profiilien valintaan">
        <span aria-hidden="true">←</span>
      </Link>
      <h1 className="routines-title">
        <span className="routines-avatar" aria-hidden="true">
          {profile.avatarEmoji}
        </span>
        {profile.name}
      </h1>
      <ul className={`routine-grid cols-${columns}`}>
        {routines.map((r, i) => (
          <li key={r.id}>
            <RoutineCard
              profileId={profile.id}
              routine={r}
              ongoing={ongoing.has(r.id)}
              color={palette[i % palette.length] ?? profile.color}
            />
          </li>
        ))}
      </ul>
    </main>
  );
}

function RoutineCard({
  profileId,
  routine,
  ongoing,
  color,
}: {
  profileId: string;
  routine: Routine;
  ongoing: boolean;
  color: string;
}) {
  return (
    <Link
      to={`/p/${profileId}/list/${routine.id}`}
      className={`routine-card${ongoing ? ' ongoing' : ''}`}
      aria-label={`${ongoing ? 'Jatka' : 'Aloita'}: ${routine.name}`}
      style={{ background: color }}
    >
      <span className="routine-card-emoji" aria-hidden="true">
        {routine.emoji}
      </span>
      <span className="routine-card-name">{routine.name}</span>
    </Link>
  );
}

// Today's ongoing routines for a profile: those started but not finished.
// Returns an empty set until IDB resolves so we don't show a stale highlight.
function useOngoingRoutineIds(profileId: Id | undefined, routines: Routine[] | undefined): Set<Id> {
  const [ids, setIds] = useState<Set<Id>>(() => new Set());

  useEffect(() => {
    if (!profileId || !routines) return;
    let cancelled = false;
    void (async () => {
      const dp = await loadDailyProgress(profileId, dateKey());
      if (cancelled) return;
      const next = new Set<Id>();
      for (const r of routines) {
        const padded = padCompleted(dp?.routines[r.id] ?? [], r.tasks.length);
        if (padded.some(Boolean) && !padded.every(Boolean)) next.add(r.id);
      }
      setIds(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [profileId, routines]);

  return ids;
}
